import { Ionicons } from '@expo/vector-icons';
import Constants from 'expo-constants';
import { router, usePathname, type Href } from 'expo-router';
import { DrawerContentScrollView } from 'expo-router/drawer';
import React, { useCallback, useMemo, useState } from 'react';
import { Image, Platform, Text, TouchableOpacity, View } from 'react-native';

import {
	allV2Items,
	groupBadgeCount,
	isNavItemActive,
	V2_BOTTOM_ITEMS,
	V2_GROUPS,
	V2_TOP_ITEMS,
	visibleNavGroups,
	type NavBadge,
	type NavGate,
	type NavGroup,
	type NavIds,
	type NavItem,
	type NavLabelKey,
} from './v2-nav-groups';

import { EnvironmentBannerDetails } from '@/components/common/environment-banner';
import { NotificationHistoryBell } from '@/components/notification-system/history-bell';
import { useTheme } from '@/components/theme-provider';
import { REVIEW_ACCENT } from '@/styles/theme';
import { crossesDrawerSection } from '@/utils/drawer-section';

/**
 * The v2 sidebar (#279) — grouped, collapsible, with live badge counts.
 *
 * Replaced the classic sidebar's flat 18-item list, which had grown past the point where
 * anyone scanned it. Structure comes from `v2-nav-groups.ts` as DATA, so the grouping is
 * reviewable in one place and a test can assert nothing was dropped in the reshuffle.
 *
 * ── STILL THE SAME DRAWER ──
 *
 * This is drawer CONTENT, not a replacement for the navigator. `app/_layout.tsx` keeps its
 * `Drawer` with `drawerType: 'permanent'` on desktop, so the icons-only collapse, the
 * mobile slide-over and every route stay exactly as they were. Only the contents change.
 *
 * Badge counts and gates are passed in rather than fetched here — `custom-drawer-content.tsx`
 * resolves them — so this file stays presentation over plain data.
 */

/** Teal — the ordinary "you have items here" count. */
const DEFAULT_BADGE_COLOR = '#00897b';
/** Red — matches the overdue red used on approval SLAs and sent-back rows. */
const OVERDUE_BADGE_COLOR = '#c62828';
/**
 * Blue — "this is yours", which is not the same claim as "this is wrong".
 *
 * Distinct from teal so a personal queue still reads as more pointed than a plain count,
 * and distinct from red because a full inbox is an ordinary Tuesday. Red here competed with
 * Overdue for the same attention and lost the distinction for both.
 */
const MINE_BADGE_COLOR = '#1976d2';
const ACTIVE = '#007AFF';

interface V2SidebarBaseProps {
	navigation: { closeDrawer: () => void };
	collapsed?: boolean;
	onToggleCollapsed?: () => void;
	/** Viewer-dependent labels, keyed the same way `NavItem.labelKey` names them. */
	labels?: Partial<Record<NavLabelKey, string>>;
	/** Org-configured record ids, keyed the same way `NavItem.idKey` names them. */
	ids?: NavIds;
	/**
	 * The management company's own mark for the brand row. `logo` is the base64 data URL
	 * uploaded in Settings → Organization (the same one printed on statements and envelopes);
	 * when there is none the row falls back to the generic icon and name.
	 */
	brand?: { logo?: string | null; name?: string | null };
}

/** The full grouped nav. */
interface V2SidebarNavProps extends V2SidebarBaseProps {
	menu?: undefined;
	/** Which gates are satisfied for this user, resolved by the caller. */
	gates: Record<NavGate, boolean>;
	/** Live counts, keyed the same way `NavItem.badge` names them. */
	badges: Partial<Record<NavBadge, number>>;
}

/**
 * A fixed list of top-level rows IN PLACE of the grouped nav — the iPhone menu (#684). Same
 * brand row, row styling and active highlight; no groups, gates or badges.
 */
interface V2SidebarMenuProps extends V2SidebarBaseProps {
	menu: NavItem[];
	gates?: undefined;
	badges?: undefined;
}

export type V2SidebarProps = V2SidebarNavProps | V2SidebarMenuProps;

export const V2Sidebar: React.FC<V2SidebarProps> = ({
	navigation, collapsed = false, onToggleCollapsed, gates, badges = {}, labels, ids, brand, menu,
}) => {
	/** The bell that reopens past toasts (#633) — at the right of the brand row, or atop the rail. */
	const headerAccessory = <NotificationHistoryBell compact={collapsed} />;
	/**
	 * The PATHNAME is the only input to active-matching.
	 *
	 * This used to also read `useGlobalSearchParams`, because rows like "Assigned to me" were
	 * query-string views of `/inbox`. A navigator cannot use `useLocalSearchParams` — it gets
	 * its own (empty) params, not the active screen's — and the global hook MERGES across the
	 * navigation state, so a stale param kept a row lit after you had left it. Those rows are
	 * routes now; see `isNavItemActive`.
	 */
	const pathname = usePathname();
	const theme = useTheme();

	/**
	 * Which groups are expanded.
	 *
	 * Seeded from `defaultOpen`, then held in state. Deliberately NOT persisted: a group a
	 * user collapsed once should not stay hidden forever across devices, and the group
	 * containing the current route is force-opened below regardless.
	 */
	const [open, setOpen] = useState<Record<string, boolean>>(() =>
		Object.fromEntries(V2_GROUPS.map((g) => [g.key, !!g.defaultOpen])));

	/**
	 * Longest-prefix wins.
	 *
	 * `/work-orders/needs-approval` starts with `/work-orders`, so a plain `startsWith`
	 * lights up BOTH rows and the sidebar shows two active items at once. Only the most
	 * specific matching route counts — the same rule a router uses.
	 */
	const resolvedItems = useMemo(() => menu ?? allV2Items(ids ?? {}), [menu, ids]);
	const isActive = useCallback(
		(item: NavItem): boolean => isNavItemActive(item, { pathname, items: resolvedItems }),
		[pathname, resolvedItems],
	);

	const navigate = useCallback((item: NavItem) => {
		const href = item.href ?? (item.name === 'index' ? '/' : `/${item.name}`);
		// Another section gets its own browser entry so Back returns here (#651); within a
		// section the click swaps the screen in place, as it always has.
		if (crossesDrawerSection(pathname, href)) router.navigate(href as Href);
		else router.replace(href as Href);
		navigation.closeDrawer();
	}, [navigation, pathname]);

	const visible = useCallback(
		(item: NavItem | NavGroup) => !item.gate || !!gates?.[item.gate],
		[gates],
	);

	/** Groups with at least one visible item, and their visible items. */
	const groups = useMemo(() => (gates ? visibleNavGroups(gates, ids) : []), [gates, ids]);

	const renderItem = (item: NavItem, inGroup: boolean) => {
		const active = isActive(item);
		// Falls back to the static label, so an unresolved key degrades to sensible copy
		// rather than an empty row.
		const label = (item.labelKey ? labels?.[item.labelKey] : undefined) ?? item.label;
		const count = item.badge ? badges[item.badge] ?? 0 : 0;
		const hasBadge = count > 0;
		/**
		 * Four registers, and each one is a different CLAIM about the count.
		 *
		 * - amber (`applications`) — the reviewer queue.
		 * - red — something has gone wrong. `overdue` is late work; `needsTriage` is mail
		 *   nobody owns, matching the inbox rail, which already renders that exact queue in
		 *   the error tone. The same queue looking urgent on one surface and routine on the
		 *   other is how people learn to trust one and ignore the other.
		 * - blue (`assignedToMe`) — this is YOURS. It wore the red above until someone
		 *   pointed out that a personal queue with mail in it is the normal state of
		 *   affairs, so it was crying wolf next to a row that genuinely meant it.
		 * - teal — an ordinary "you have items here" count.
		 */
		const badgeColor = item.badge === 'applications'
			? REVIEW_ACCENT
			: item.badge === 'assignedToMe'
				? MINE_BADGE_COLOR
				: (item.badge === 'overdue' || item.badge === 'needsTriage')
					? OVERDUE_BADGE_COLOR
					: DEFAULT_BADGE_COLOR;

		return (
			<TouchableOpacity
				key={item.name}
				testID={`drawer-item-${item.name}`}
				onPress={() => navigate(item)}
				accessibilityLabel={label}
				style={{
					flexDirection: 'row',
					alignItems: 'center',
					justifyContent: collapsed ? 'center' : 'flex-start',
					paddingVertical: 9,
					// Children indent under their group heading, so the hierarchy reads without
					// a connector line; top-level rows share the headings' left edge. No indent
					// at all when collapsed to an icon rail.
					paddingLeft: collapsed ? 0 : (inGroup ? 34 : 14),
					paddingRight: collapsed ? 0 : 12,
					backgroundColor: active ? 'rgba(0, 122, 255, 0.1)' : 'transparent',
					borderRadius: 8,
					marginHorizontal: collapsed ? 6 : 8,
					marginVertical: 1,
				}}
			>
				{collapsed ? (
					<View>
						<Ionicons name={item.icon} size={22} color={active ? ACTIVE : '#666'} />
						{hasBadge && (
							<View style={{
								position: 'absolute', top: -6, right: -8,
								backgroundColor: badgeColor, borderRadius: 8, minWidth: 16, height: 16,
								alignItems: 'center', justifyContent: 'center', paddingHorizontal: 4,
							}}>
								<Text style={{ color: '#fff', fontSize: 10, fontWeight: 'bold' }}>{count}</Text>
							</View>
						)}
					</View>
				) : (
					<>
						{/*
							Icon on TOP-LEVEL rows only (Dashboard, Settings), matching the
							mockup. Group children are indented text: they already sit under an
							icon-bearing heading, and a second column of icons there turns the
							hierarchy back into the flat list this replaced.
						*/}
						{!inGroup && (
							<Ionicons
								name={item.icon}
								size={20}
								color={active ? ACTIVE : '#37474f'}
								style={{ marginRight: 10 }}
							/>
						)}
						<Text style={{
							color: active ? ACTIVE : (inGroup ? '#555' : '#263238'),
							fontWeight: active ? '700' : (inGroup ? '400' : '600'),
							fontSize: inGroup ? 14 : 15,
							flex: 1,
							// A flex child in a row defaults to `min-width: auto` on RN-Web, so it
							// refuses to shrink below its text and shoves the badge out of the row
							// instead of truncating. The group rows carry the longest labels
							// ("Location Analytics", "Documents Admin") and indent 34px on top.
							minWidth: 0,
						}}>
							{label}
						</Text>
						{hasBadge && (
							<View style={{
								backgroundColor: badgeColor, borderRadius: 5, minWidth: 22, height: 20,
								alignItems: 'center', justifyContent: 'center', paddingHorizontal: 6,
							}}>
								<Text style={{ color: '#fff', fontSize: 11, fontWeight: 'bold' }}>{count}</Text>
							</View>
						)}
					</>
				)}
			</TouchableOpacity>
		);
	};

	const renderGroup = (group: NavGroup & { items: NavItem[] }) => {
		/**
		 * A group gated down to ONE row is a header over a single link — Inbox holding only
		 * Documents on an iPad, Accounting holding only Associations for someone without the
		 * accounting write right. Render that row at the top level, in the group's place, and
		 * drop the header; it keeps its own icon, badge and active highlight.
		 */
		if (group.items.length === 1) {
			return <React.Fragment key={group.key}>{renderItem(group.items[0], false)}</React.Fragment>;
		}
		/**
		 * A group holding the current route is always expanded, whatever the user last did —
		 * otherwise the sidebar shows no indication of where you are.
		 *
		 * Matched on the PATHNAME, not on a child being active. Those are different
		 * questions: exactly one row highlights, but a route can be inside a group while no
		 * row owns it — the inbox's away-places (Archived, Spam, Waiting) have no row of
		 * their own, and judging by `isActive` collapsed the whole Inbox group the moment
		 * you opened one of them.
		 */
		const holdsRoute = group.items.some((i) => pathname === `/${i.name}` || pathname.startsWith(`/${i.name}/`));
		const expanded = collapsed || open[group.key] || holdsRoute;
		// Roll child counts up to the heading, so a collapsed group still shows there is
		// work inside it. Without this, collapsing a group hides its badge and the work
		// with it.
		const rolledUp = groupBadgeCount(group, badges);

		return (
			<View key={group.key}>
				{!collapsed && (
					<TouchableOpacity
						testID={`drawer-group-${group.key}`}
						onPress={() => setOpen((prev) => ({ ...prev, [group.key]: !expanded }))}
						accessibilityLabel={`${group.label} section`}
						style={{
							flexDirection: 'row',
							alignItems: 'center',
							gap: 10,
							paddingVertical: 11,
							paddingHorizontal: 14,
							marginHorizontal: 8,
							marginTop: 6,
						}}
					>
						<Ionicons name={group.icon} size={20} color="#37474f" />
						{/* minWidth: 0 — see the row label above; without it the rolled-up count
							gets pushed out of the header rather than the label truncating. */}
						<Text style={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: '600', color: '#263238' }}>
							{group.label}
						</Text>
						{!expanded && rolledUp > 0 && (
							<View style={{
								backgroundColor: DEFAULT_BADGE_COLOR, borderRadius: 5, minWidth: 22, height: 20,
								alignItems: 'center', justifyContent: 'center', paddingHorizontal: 6,
							}}>
								<Text style={{ color: '#fff', fontSize: 11, fontWeight: 'bold' }}>{rolledUp}</Text>
							</View>
						)}
						<Ionicons name={expanded ? 'chevron-up' : 'chevron-down'} size={16} color="#90a4ae" />
					</TouchableOpacity>
				)}
				{expanded && group.items.map((i) => renderItem(i, !collapsed))}
			</View>
		);
	};

	return (
		<View style={{ flex: 1, paddingTop: Platform.OS === 'web' ? 12 : 20 }} testID="v2-sidebar">
			{/*
				Brand row. The organization's own logo when one has been uploaded — this is the
				management company's portal, not ours — otherwise the generic mark and name.
			*/}
			{!collapsed && (
				<View style={{
					flexDirection: 'row', alignItems: 'center', gap: 10,
					paddingHorizontal: 18, paddingBottom: 14,
				}} testID="v2-sidebar-brand">
					{brand?.logo ? (
						<Image
							source={{ uri: brand.logo }}
							style={{ width: 200, height: 44, flexShrink: 1 }}
							resizeMode="contain"
							accessibilityLabel={brand.name ?? 'Organization logo'}
							testID="v2-sidebar-brand-logo"
						/>
					) : (
						<>
							<View style={{
								width: 30, height: 30, borderRadius: 8, backgroundColor: ACTIVE,
								alignItems: 'center', justifyContent: 'center',
							}}>
								<Ionicons name="business" size={18} color="#fff" />
							</View>
							<Text style={{ fontSize: 17, fontWeight: '700', color: '#1a2027', flexShrink: 1 }} numberOfLines={1}>
								{brand?.name || 'PropertyPortal'}
							</Text>
						</>
					)}
					<View style={{ marginLeft: 'auto' }}>{headerAccessory}</View>
				</View>
			)}
			{collapsed && <View style={{ alignItems: 'center', paddingBottom: 8 }}>{headerAccessory}</View>}

			<DrawerContentScrollView>
				{!collapsed && <EnvironmentBannerDetails />}
				{menu ? menu.map((i) => renderItem(i, false)) : (
					<>
						{V2_TOP_ITEMS.filter(visible).map((i) => renderItem(i, false))}
						{groups.map(renderGroup)}

						<View style={{
							height: 1,
							backgroundColor: theme.colors.outline,
							marginHorizontal: collapsed ? 12 : 16,
							marginVertical: 12,
						}} />
						{V2_BOTTOM_ITEMS.filter(visible).map((i) => renderItem(i, false))}
					</>
				)}
			</DrawerContentScrollView>

			{onToggleCollapsed && (
				<TouchableOpacity
					testID="btn-toggle-drawer-collapse"
					onPress={onToggleCollapsed}
					accessibilityLabel={collapsed ? 'Expand menu' : 'Collapse menu'}
					style={{
						flexDirection: 'row',
						alignItems: 'center',
						justifyContent: collapsed ? 'center' : 'flex-start',
						paddingVertical: 12,
						paddingHorizontal: collapsed ? 0 : 16,
						marginHorizontal: collapsed ? 6 : 8,
						borderTopWidth: 1,
						borderTopColor: theme.colors.outline,
					}}
				>
					<Ionicons name={collapsed ? 'chevron-forward' : 'chevron-back'} size={22} color="#666" />
					{!collapsed && <Text style={{ color: '#666', marginLeft: 12 }}>Collapse</Text>}
				</TouchableOpacity>
			)}

			{!collapsed && (
				<Text
					testID="drawer-app-version"
					style={{ fontSize: 11, color: '#999', textAlign: 'center', paddingVertical: 8 }}
				>
					v{Constants.expoConfig?.version ?? 'dev'}
				</Text>
			)}
		</View>
	);
};

export default V2Sidebar;
